/**
 * 頁 18 — 「我嘅資料」（供應商；P1.5-A；`decisions/015` §2.8）
 *
 * 產品負責人 2026-09-28：「create the form first, have basic information, **can add later on**」
 *   → 基本欄位（名／聯絡人／電話／WhatsApp）＋ **`attrs` 自訂欄位**（JSON，之後加欄位唔使 migration）
 *
 * 紅線
 *   · ⚠️ `code`（供應商代號）**唯讀** —— 係身分（單號／對數都靠佢）；要改 ＝ 管理員喺主檔處理
 *   · 只有登入嘅供應商本人（或 admin）入得（後端 `/api/me` 一樣有 `guard` ＋ `supplierScope`）
 *   · 三語齊（`check:locales` 咬）；顏色一律 ui-kit tokens
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { btnPrimary, card, errText, hint as hintStyle, input, label } from '@coeldery/ui-kit';
import MyItemsPanel from '../components/MyItemsPanel';
import { ApiError, api, type Me, type MySupplier } from '../lib/api';

export default function MeProfile({ user }: { user: Me }) {
  const { t } = useTranslation();
  const [data, setData] = useState<MySupplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [waNumber, setWaNumber] = useState('');

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.mySupplier(user.role === 'admin' ? user.supplierId ?? undefined : undefined);
      setData(res.supplier);
      setName(res.supplier.name ?? '');
      setContactName(res.supplier.contactName ?? '');
      setContactPhone(res.supplier.contactPhone ?? '');
      setWaNumber(res.supplier.waNumber ?? '');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, user.role, user.supplierId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.updateMySupplier({ name, contactName, contactPhone, waNumber });
      setData(res.supplier);
      setNotice(t('me.saved'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p style={hintStyle}>{t('common.loading')}</p>;

  if (!data) {
    return (
      <section style={{ ...card, padding: '16px' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 800 }}>{t('me.title')}</h2>
        {error && (
          <p style={errText} role="alert">
            {error}
          </p>
        )}
      </section>
    );
  }

  const attrs = Object.entries(data.attrs ?? {});

  return (
    <>
      <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800 }}>{t('me.title')}</h2>
      <p style={hintStyle}>{t('me.intro')}</p>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
      {notice && <p style={{ ...hintStyle, fontWeight: 700, color: 'var(--color-primary)' }}>{notice}</p>}

      <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
        <div style={{ marginBottom: '12px' }}>
          <label style={label} htmlFor="me-code">
            {t('me.code')}
          </label>
          <input id="me-code" style={{ ...input, backgroundColor: 'var(--color-off-bg)' }} value={data.code} readOnly />
          <p style={hintStyle}>{t('me.codeHint')}</p>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={label} htmlFor="me-name">
            {t('me.name')}
          </label>
          <input id="me-name" style={input} value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={label} htmlFor="me-contact">
            {t('me.contactName')}
          </label>
          <input
            id="me-contact"
            style={input}
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={label} htmlFor="me-phone">
            {t('me.contactPhone')}
          </label>
          <input
            id="me-phone"
            style={input}
            inputMode="tel"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={label} htmlFor="me-wa">
            {t('me.waNumber')}
          </label>
          <input
            id="me-wa"
            style={input}
            inputMode="tel"
            placeholder="852…"
            value={waNumber}
            onChange={(e) => setWaNumber(e.target.value)}
          />
          <p style={hintStyle}>{t('me.waHint')}</p>
        </div>

        <button type="button" style={btnPrimary} onClick={() => void save()} disabled={busy}>
          {busy ? t('common.saving') : t('common.save')}
        </button>
      </section>

      {/* 自訂欄位（P1-4 留嘅 `attrs`；之後加欄位唔使 migration） */}
      <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>{t('me.attrsTitle')}</h3>
        <p style={hintStyle}>{t('me.attrsHint')}</p>
        {attrs.length === 0 ? (
          <p style={hintStyle}>{t('me.attrsEmpty')}</p>
        ) : (
          <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
            {attrs.map(([k, v]) => (
              <li key={k} style={{ fontSize: '14px' }}>
                <strong>{k}</strong>：{typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ⭐ P1.5-D：我嘅貨品（清單 ＋ 新增）—— 供應商自己加得到貨，唔使先開單 */}
      {user.supplierId && <MyItemsPanel user={user} />}
    </>
  );
}
